import React, { useState, useEffect } from 'react';
import { Star, Quote, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { getTestimonials } from '../api';

const Testimonials = () => {
  const { t, i18n } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getTestimonials();
        setTestimonials(data);
      } catch (err) {
        setError(t('testimonials.error'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  if (loading) {
    return (
      <section id="testimonials" className="py-24 bg-white">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-12 h-12 animate-spin text-pink-500" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="testimonials" className="py-24 bg-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-yellow-500 via-orange-600 to-pink-600 bg-clip-text text-transparent">
              {t('testimonials.trustTitle')}
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {t('testimonials.trustSubtitle')}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card
              key={testimonial.id}
              className="relative hover:shadow-2xl transition-all duration-300 border-0 shadow-lg p-8"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <Quote className="w-16 h-16 text-pink-500" />
              </div>

              <CardContent className="p-0">
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-yellow-400"
                    />
                  ))}
                </div>

                {/* Content */}
                <p className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "{i18n.language === 'en' ? (testimonial.content_en || testimonial.content) : testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image.startsWith('/api/') 
                      ? `${process.env.REACT_APP_BACKEND_URL}${testimonial.image}` 
                      : testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover border-4 border-pink-100"
                  />
                  <div>
                    <div className="font-bold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {i18n.language === 'en' ? (testimonial.role_en || testimonial.role) : testimonial.role}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-2">
              500+
            </div>
            <div className="text-gray-600 text-sm">{t('testimonials.stats.satisfiedClients')}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-fuchsia-600 bg-clip-text text-transparent mb-2">
              5.0
            </div>
            <div className="text-gray-600 text-sm">{t('testimonials.stats.perfectRating')}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-fuchsia-600 to-orange-600 bg-clip-text text-transparent mb-2">
              100%
            </div>
            <div className="text-gray-600 text-sm">{t('testimonials.stats.recommendation')}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-yellow-500 to-orange-600 bg-clip-text text-transparent mb-2">
              24h
            </div>
            <div className="text-gray-600 text-sm">{t('testimonials.stats.quickResponse')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
